'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getActiveStrategyId, getStrategy, listStrategies } from '@/lib/strategyStudioV2Api';
import { humanizeApiError } from '@/lib/humanizeApiError';

const LAST_STRATEGY_KEY = 'helix.decisionLedger.lastStrategyId';

function tryReadLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)?.trim() || null;
  } catch {
    return null;
  }
}

function tryRemoveLocalStorage(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // noop
  }
}

async function isAccessibleStrategyId(strategyId: string): Promise<boolean> {
  const id = String(strategyId ?? '').trim();
  if (!id) return false;
  try {
    await getStrategy(id);
    return true;
  } catch (err: unknown) {
    const human = humanizeApiError(err);
    if (human.statusCode === 401 || human.statusCode === 403 || human.statusCode === 404) return false;
    throw err;
  }
}

export default function DecisionLedgerEntryClient({ strategyId }: { strategyId?: string }) {
  const router = useRouter();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);

    const resolveStrategyId = async (): Promise<string | null> => {
      const explicit = String(strategyId ?? '').trim();
      if (explicit) return explicit;

      const stored = tryReadLocalStorage(LAST_STRATEGY_KEY);
      if (stored) {
        const ok = await isAccessibleStrategyId(stored);
        if (ok) return stored;
        tryRemoveLocalStorage(LAST_STRATEGY_KEY);
      }

      try {
        const active = await getActiveStrategyId();
        if (active) {
          const ok = await isAccessibleStrategyId(active);
          if (ok) return active;
        }
      } catch {
        // ignore
      }

      const strategies = await listStrategies();
      const firstId = String(strategies?.[0]?.id ?? '').trim();
      return firstId || null;
    };

    void resolveStrategyId()
      .then((resolvedStrategyId) => {
        if (cancelled) return;
        if (!resolvedStrategyId) {
          router.replace('/strategy-studio/strategies');
          return;
        }

        try {
          window.localStorage.setItem(LAST_STRATEGY_KEY, resolvedStrategyId);
        } catch {
          // noop
        }

        router.replace(`/strategy-studio/strategies/${encodeURIComponent(resolvedStrategyId)}/decision-ledger`);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to open Decision Ledger.';
        setStatus('error');
        setError(message);
      });

    return () => {
      cancelled = true;
    };
  }, [router, strategyId]);

  return (
    <main className="mx-auto max-w-3xl p-6" data-testid="decision-ledger-entry">
      <div className="space-y-2" data-testid="decision-ledger-entry-frame">
        <h1 className="text-xl font-semibold">Opening Decision Ledger.</h1>
        <p className="text-sm text-muted-foreground">Resolving the latest strategy for your tenant.</p>
        {status === 'error' ? (
          <p className="text-sm text-destructive" data-testid="decision-ledger-entry-error">
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}
